import { provideDiTokenValue } from '@bcf-vanilla-ts-v1-shared/di/di-token';
import { provideSingleton } from '@bcf-vanilla-ts-v1-shared/di/provide-singleton';
import { isSSR } from '@bcf-vanilla-ts-v1-shared/misc/utils/is-ssr';
import type { Subscriber } from 'rxjs';
import { EMPTY, fromEvent, Observable, pluck } from 'rxjs';
import { globalThisEventTarget } from '../common/global-this-event-target';
import { WORKER_INSTANCE } from './tokens';

export abstract class WorkerWrapper {
  // biome-ignore lint/suspicious/noEmptyBlockStatements: must be that way
  public postMessage(_message: Record<string, any>): void {}

  public getMessage<T>(): Observable<T> {
    return EMPTY;
  }
}

export class WorkerWrapperInternal extends WorkerWrapper {
  constructor(private _worker: Worker) {
    super();
  }

  public override postMessage(message: Record<string, any>): void {
    try {
      this._worker.postMessage(this._tryParseMessage(message));
    } catch (_err: any) {
      if (this._dismissError(message)) {
        return;
      }
      console.error('BROWSER_SIDE # cloning problem message', message);
      throw new Error('BROWSER_SIDE # problem with cloning message from browser to worker');
    }
  }

  public override getMessage<T>(): Observable<T> {
    return new Observable((subscriber: Subscriber<T>) =>
      this._worker.addEventListener('message', (message: MessageEvent<T>) => subscriber.next(message.data))
    );
  }

  private _dismissError(message: Record<string, any>): boolean {
    if (message['channelName'] === '_workerSentryErrorHandler' && message['payload']?.['target'] instanceof WebSocket) {
      return true;
    }
    return false;
  }

  /**
   * True only for a FOREIGN http-error-shaped object (e.g. Angular's `HttpErrorResponse`, a class
   * instance that is not structured-cloneable) which must be flattened before `postMessage`.
   *
   * Our own `HttpError` (`api-access/common/http-client.ts`) is deliberately excluded: it is a
   * plain object literal whose every field is structured-cloneable, so it crosses the boundary
   * untouched - flattening it dropped `_type`/`error`/`errorAsString`/`responseHeaders` and broke
   * `isHttpError()` on the receiving side (BCF-V2-PREVIEW-6F / -5R).
   */
  private _isHttpErrorResponse(message: Record<string, any>): boolean {
    // `_type` tag kept in sync with HttpError in api-access/common/http-client.ts; importing
    // isHttpError() here would make platforms depend on api-access, which already depends on
    // platforms.
    if (message['_type'] === '_HttpError') {
      return false;
    }
    if (message.hasOwnProperty('url') && message.hasOwnProperty('status')) {
      return true;
    }
    return false;
  }

  private _tryParseMessage(message: Record<string, any>): Record<string, any> {
    if (message.payload && this._isHttpErrorResponse(message.payload)) {
      message.payload = {
        url: message.payload.url,
        status: message.payload.status,
        message: message.payload.statusText ?? message.payload.errorAsString
      };
    }
    return message;
  }
}

class WorkerWrapperVirtual extends WorkerWrapper {
  constructor(private _eventTarget: EventTarget) {
    super();
  }

  public override postMessage(message: Record<string, any>): void {
    this._eventTarget.dispatchEvent(new CustomEvent('virtualWorkerChannel', { detail: message }));
  }
  public override getMessage<T>(): Observable<T> {
    return fromEvent<CustomEvent<T>>(this._eventTarget, 'virtualWorkerChannel').pipe(pluck('detail'));
  }
}

// Decision: see ADR-0002 — the virtual (same-thread) transport is a migration bridge, not the target design.
export function provideWorkerWrapper(): WorkerWrapper {
  if (isSSR()) {
    return provideSingleton(WorkerWrapper, () => new WorkerWrapperVirtual(globalThisEventTarget()));
  }
  return provideSingleton(WorkerWrapper, () => new WorkerWrapperInternal(provideDiTokenValue(WORKER_INSTANCE)));
}
